import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Loader2, ChevronLeft, ChevronRight, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/adminApi';
import { EmptyState } from '@/components/admin/EmptyState';

export default function AdminSubscribers() {
  const [data, setData] = useState({ subscribers: [], total: 0, page: 1, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchSubs = useCallback(() => {
    setLoading(true);
    api.get('/admin/subscribers', { params: { page, limit: 50 } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const removeSub = async (s) => {
    if (!window.confirm(`Remove ${s.email} from the newsletter list?`)) return;
    try {
      await api.delete(`/admin/subscribers/${s.id}`);
      toast.success('Subscriber removed');
      fetchSubs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    const rows = [['email', 'subscribed_at'], ...data.subscribers.map((s) => [s.email, s.created_at])];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'copyclick-subscribers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.limit));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Newsletter Subscribers</h1>
          <p className="mt-1 text-sm text-white/50">{data.total} real sign-ups captured from your marketing site.</p>
        </div>
        {data.total > 0 && (
          <button onClick={exportCsv} data-testid="admin-subscribers-export" className="flex items-center gap-2 rounded-xl border border-white/10 bg-elevated px-4 py-2 text-sm text-white/80 hover:bg-white/5">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : data.total === 0 ? (
        <EmptyState icon={Mail} title="No subscribers yet" description="When visitors join your newsletter from the site footer, they'll show up here in real time. Share your site to start collecting leads." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface/70">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
              <tr>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Subscribed</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.subscribers.map((s) => (
                <tr key={s.id} data-testid="admin-subscriber-row" className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/15 text-brand"><Mail className="h-4 w-4" /></span>
                      <span className="text-white">{s.email}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-white/60">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end">
                      <button onClick={() => removeSub(s)} data-testid="admin-subscriber-delete" title="Remove subscriber" className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:bg-destructive/10 hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-white/10 px-5 py-3 text-sm text-white/50">
            <span>Page {data.page} of {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-white/10 p-1.5 hover:bg-white/5 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-white/10 p-1.5 hover:bg-white/5 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
